import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getIsUngatedForSeats } from 'pricing-pages-lib/utils/gatingUtils';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { useSelector } from 'react-redux';
import ApiNameToUpgradeProductMap from 'self-service-api/constants/ApiNameToUpgradeProductMap';
import { upgradeProductToTrialIds } from 'self-service-api/constants/Trials';
import { getAccountDashboardUrl } from 'self-service-api/core/utilities/links';
import styled from 'styled-components';
import Small from 'UIComponents/elements/Small';
import UIImage from 'UIComponents/image/UIImage';
import UILink from 'UIComponents/link/UILink';
import { HighlightedProductsHeaderConfig } from '../../constants/merchCards/HighlightedProductsHeaderConfig';
import { MERCH_CARD } from '../../constants/tracking/Locations';
import { useAppSelector } from '../../reducers';
import { getIsCurrentTrialSelector } from '../../selectors/ActiveTrialsSelectors';
import { hasPricingAndPurchasingSucceededSelector, hasPurchaseMotionsFailedSelector, isMerchCardCTAsDataLoadingSelector } from '../../selectors/LoadingStateSelectors';
import { crmSuiteOwnershipLevel, hasProductVersionInBundledProductInventorySelector, hasProductVersionInProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { productSelector } from '../../selectors/ProductsSelectors';
import { usePricingLibSelector } from '../../selectors/SelectorUtils';
import { shouldShowTermSelectorForMerchSelector } from '../../selectors/TermSelectors';
import { useQueryParamTerm } from '../../utils/urls/queryParamUtils';
import CurrentPlanTag from '../common/CurrentPlanTag';
import DetailedTermSelector from '../common/DetailedTermSelector';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import { SectionTitle } from '../common/SharedComponents';
import CompareCheckbox from '../compare/CompareCheckbox';
import AdditionalUnitsPicker from '../plan-builder/AdditionalUnitsPicker';
import RequiredOnboardingSubtextContainer from '../plan-builder/RequiredOnboardingSubtextContainer';
import BusinessObjectivesContainer from './business-objectives/BusinessObjectivesContainer';
import FallbackMerchCardCTAsContainer from './FallbackMerchCardCTAsContainer';
import MerchCardCTAsContainer from './MerchCardCTAsContainer';
import { MerchCardWrapper, Section } from './MerchCardSharedComponents';
import HighlightedMerchPriceDisplay from './price-display/HighlightedMerchPriceDisplay';
import { useSkuAdditionalUnits } from './useSkuAdditionalUnits';
const StyledTermSelectorContainer = styled(DetailedTermSelector).withConfig({
  displayName: "HighlightedMerchCard__StyledTermSelectorContainer",
  componentId: "vf3nb2-0"
})(["max-width:300px;margin:0 auto;"]);

const HighlightedMerchCard = ({
  productLevel,
  productLine,
  commonStyles,
  selectedTab,
  disclaimer
}) => {
  const isSeatsRedesignUngated = usePricingLibSelector(getIsUngatedForSeats);
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const isMerchCardCTAsDataLoading = useSelector(isMerchCardCTAsDataLoadingSelector);
  const hasPurchaseMotionsFailed = useSelector(hasPurchaseMotionsFailedSelector);
  const product = useSelector(state => productSelector(state, {
    productLine,
    productLevel
  }));
  const apiName = product && product.apiName;
  const upgradeProduct = ApiNameToUpgradeProductMap[apiName];
  let trialId;

  if (upgradeProduct !== undefined) {
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    trialId = upgradeProductToTrialIds[upgradeProduct];
  }

  const isCurrentTrial = useSelector(state => {
    return getIsCurrentTrialSelector(state, trialId);
  });
  const seleniumId = apiName || 'loading';
  const {
    additionalUnitSkuId,
    isAdditionalUnitSkuIdAQuantityPack,
    additionalUnitQuantity,
    setAdditionalUnitQuantity,
    skuId
  } = useSkuAdditionalUnits({
    productLevel,
    productLine
  });
  const ownsProductVersion = useAppSelector(state => hasPricingAndPurchasingSucceeded && hasProductVersionInProductInventorySelector(state, {
    skuId
  }));
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const {
    title = '',
    subtitle = '',
    icon = ''
  } = HighlightedProductsHeaderConfig[productLine][productLevel] || {};
  const shouldShowTermSelector = useSelector(state => skuId && shouldShowTermSelectorForMerchSelector(state, {
    skuId
  }));
  const additionalUnitsPickerProps = {
    additionalUnitSkuId,
    additionalUnitQuantity,
    setAdditionalUnitQuantity,
    productConfigurations: [createProductConfig({
      skuId: skuId || 0,
      quantity: 1
    })],
    skuId,
    compact: true,
    queryParamTerm,
    trackingSource: MERCH_CARD,
    productLevel
  };
  const ownsProductViaBundle = useSelector(state => hasPricingAndPurchasingSucceeded && hasProductVersionInBundledProductInventorySelector(state, {
    skuId
  }));
  const suiteOwnershipLevel = useAppSelector(state => hasPricingAndPurchasingSucceeded && crmSuiteOwnershipLevel(state)); // In order for the grid to work, the wrapper element requires display: contents.
  // Children of the display grid item must get the grid-start attributes directly

  return /*#__PURE__*/_jsxs(MerchCardWrapper, {
    children: [/*#__PURE__*/_jsxs(Section, {
      className: "flex flex-column p-top-8",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 1,
        position: 'relative'
      }),
      "data-selenium-id": `${seleniumId}-merch-card`,
      children: [(ownsProductVersion || ownsProductViaBundle) && /*#__PURE__*/_jsx(CurrentPlanTag, {
        message: ownsProductViaBundle ? `pricingPage.highlightedMerchCards.youOwnSuite.${suiteOwnershipLevel}` : 'pricingPage.highlightedMerchCards.currentPlan'
      }), isCurrentTrial && /*#__PURE__*/_jsx(CurrentPlanTag, {
        message: "pricingPage.highlightedMerchCards.currentTrial"
      }), /*#__PURE__*/_jsxs("div", {
        className: "display-flex align-center",
        children: [icon && /*#__PURE__*/_jsx(UIImage, {
          className: "m-right-3",
          src: icon
        }), /*#__PURE__*/_jsx(SectionTitle, {
          className: "m-bottom-0",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: title
          })
        })]
      })]
    }), /*#__PURE__*/_jsx(Section, {
      className: "flex flex-column p-top-6",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 2,
        position: 'relative'
      }),
      children: isSeatsRedesignUngated && ownsProductVersion ? /*#__PURE__*/_jsx(UILink, {
        href: getAccountDashboardUrl(),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.viewAccountButton.text"
        })
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: subtitle
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 3
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [20, 33, 20],
        widths: [60, 80, 140],
        className: "flex-column",
        Content: HighlightedMerchPriceDisplay,
        contentProps: {
          queryParamTerm,
          skuId,
          useLargePrice: true,
          quantityPackSkuId: isAdditionalUnitSkuIdAQuantityPack ? additionalUnitSkuId : null,
          quantityPackQuantity: isAdditionalUnitSkuIdAQuantityPack ? additionalUnitQuantity : 0
        }
      })
    }), !isSeatsRedesignUngated && /*#__PURE__*/_jsx(Section, {
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 4
      }),
      children: shouldShowTermSelector && /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [41],
        widths: [300],
        className: "flex-column align-center",
        Content: StyledTermSelectorContainer,
        contentProps: {
          skuIds: isAdditionalUnitSkuIdAQuantityPack && additionalUnitQuantity > 0 ? [skuId, additionalUnitSkuId] : [skuId],
          onChange: setQueryParamTerm,
          activeTerm: queryParamTerm,
          trackingSource: MERCH_CARD
        }
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 5
      }),
      children: (!isSeatsRedesignUngated || !ownsProductVersion) && /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        className: "flex-column align-start",
        heights: [34, 76],
        widths: ['75%', '100%'],
        Content: AdditionalUnitsPicker,
        contentProps: additionalUnitsPickerProps
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 6,
        paddingBottom: '18px'
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: isMerchCardCTAsDataLoading,
        isError: hasPurchaseMotionsFailed,
        heights: [47],
        Content: MerchCardCTAsContainer,
        contentProps: {
          queryParamTerm,
          skuId,
          productLine,
          additionalUnitSkuId,
          additionalUnitQuantity: isAdditionalUnitSkuIdAQuantityPack ? additionalUnitQuantity : 0,
          ctaStyles: {
            width: '100%',
            lineHeight: '23px'
          }
        },
        ErrorContent: FallbackMerchCardCTAsContainer,
        errorContentProps: {
          skuId
        }
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "flex flex-column align-center",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 7
      }),
      children: apiName && /*#__PURE__*/_jsx(CompareCheckbox, {
        apiName: apiName
      })
    }), /*#__PURE__*/_jsx(BusinessObjectivesContainer, {
      productLine: productLine,
      productLevel: productLevel,
      apiName: apiName,
      commonStyles: commonStyles
    }), /*#__PURE__*/_jsxs(Section, {
      className: "flex flex-column align-center p-bottom-6 p-top-3",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 10
      }),
      children: [skuId && /*#__PURE__*/_jsx(RequiredOnboardingSubtextContainer, {
        queryParamTerm: queryParamTerm,
        skuIds: [skuId],
        tab: selectedTab,
        trackingSource: MERCH_CARD,
        includesOnboardingCost: false
      }), disclaimer && /*#__PURE__*/_jsx(Small, {
        use: "help",
        className: "align-end p-bottom-6",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: disclaimer
        })
      })]
    })]
  });
};

export default HighlightedMerchCard;